import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { getCumulativeOffset, getCumulativeTransformations } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import styles from "./AnchorTarget.module.scss";

/**
 * AnchorTarget renders children content adjacent to another DOM element (Anchor). Anchor utilizes
 * the native browser anchor capabilities, but only allows a subset of its options in pursuit of
 * simplification. Anchor and AnchorTarget must be used together.
 *
 * NOTE:
 * A classname must be given which contains a position-anchor property. This should be something
 * like:
 *
 * .classPassedToAnchorTarget {
 *    position-anchor: --my-anchor;
 *  }
 *
 *  This name, '--my-anchor' in the example above, must match the anchor-name given to the Anchor
 *  component via its className. See Anchor for info.
 *
 * TODO:
 * The className with position-anchor requirement is temporary until browser support is widely
 * implemented. This requirement can then go away with PHX-2817.
 */

export const AnchorTarget = (props) => {
  const { children, className, location = "hidden", name, transform } = props;
  const [anchorOffsetPosition, setAnchorOffsetPosition] = useState(0);
  const ref = useRef();

  useEffect(() => {
    setAnchorOffsetPosition({
      left:
        getCumulativeOffset(ref.current).left +
        getCumulativeTransformations(ref.current).transformX,
      top:
        getCumulativeOffset(ref.current).top + getCumulativeTransformations(ref.current).transformY,
    });
  }, [location]);

  return (
    <div className={getClass()} ref={ref} style={getStyle()}>
      {children}
    </div>
  );

  function getClass() {
    return classNames(
      {
        [styles.above]: location === "above",
        [styles.aboveLeft]: location === "above left",
        [styles.aboveRight]: location === "above right",
        [styles.below]: location === "below",
        [styles.belowLeft]: location === "below left",
        [styles.belowRight]: location === "below right",
        [styles.center]: location === "center",
        [styles.hidden]: location === "hidden",
        [styles.left]: location === "left",
        [styles.right]: location === "right",
      },
      styles.target,
      className
    );
  }

  function getStyle() {
    const { left, top } = anchorOffsetPosition;

    return {
      "--offset-position-x": `-${left}px`,
      "--offset-position-y": `-${top}px`,
      positionAnchor: `--${name}`,
      transform,
    };
  }
};

AnchorTarget.propTypes = {
  /**
   * Target content to be placed adjacent to the anchor.
   *
   * For example:
   * <Anchor className={styles.anchor} name="my-name">Anchor</Anchor>
   * <AnchorTarget className={styles.target} location="right" name="my-name">Target</AnchorTarget>
   */
  children: PropTypes.node.isRequired,

  /**
   * Additional classes for positioning the component. It must contain a position-anchor property.
   * Something like:
   * position-anchor: --my-anchor;
   *
   * This is required until browser support is widely implemented.
   */
  className: PropTypes.string,

  /** Which adjacent side to render target relative to anchor. */
  location: PropTypes.oneOf([
    "above",
    "above left",
    "above right",
    "below",
    "below left",
    "below right",
    "center",
    "hidden",
    "left",
    "right",
  ]),

  /**
   * Name that will be used to tie Anchor and AnchorTarget together. Both Anchor and AnchorTarget
   * must receive the same name prop value.
   */
  name: PropTypes.string.isRequired,

  /** String to set outmost div element's transform property. Something like: "translateX(50px)". */
  transform: PropTypes.string,
};
